import React, { Fragment, useState } from 'react';
import FileUpload from './FileUpload';
import { convertFileToBase64 } from '../utils';
import ConfirmationPage, { FormState } from '../../ConfirmationPage';
import { uploadDocument } from '../../../services/vault/document';
import { StripeEnv } from '../../../util/enums';
import useIsMobile from '../hooks/useIsMobile';
import logger from '../../../util/logger';
import { SnapIcon } from '../../SnapIcon';

export default function PersonalIdentityComponent({
  stripeConnectAccountId,
  stripePersonId,
  kycId,
  gateway,
  stripeEnv = StripeEnv.RAISE,
  onSuccess,
}: {
  stripeConnectAccountId: string;
  stripePersonId: string;
  kycId: string;
  gateway: VaultProvider;
  stripeEnv?: StripeEnv;
  onSuccess?: () => void;
}): JSX.Element {
  const [selectedRadio, setSelectedRadio] = useState(0);
  const [selectedDocFront, setSelectedDocFront] = useState<File>();
  const [selectedDocBack, setSelectedDocBack] = useState<File>();
  const [submitState, setSubmitState] = useState<FormState>();
  const [submitErrors, setSubmitErrors] = useState<ApiError[]>([]);
  const [showRequiredMessageFront, setShowRequiredMessageFront] =
    useState(false);
  const [showRequiredMessageBack, setShowRequiredMessageBack] = useState(false);
  const isMobile = useIsMobile();

  const PERSONAL_IDENTITY_DOCS = [
    {
      label: `State-issued driver's license (preferred)`,
      value: 'driver_license',
      checked: false,
    },
    {
      label: 'Valid passport or passport card',
      value: 'passport',
      checked: false,
    },
    {
      label: 'Social security card',
      value: 'social_security_card',
      checked: false,
    },
  ];

  const onSubmit = async (): Promise<void> => {
    if (selectedDocFront === undefined || selectedDocBack === undefined) {
      setShowRequiredMessageFront(selectedDocFront === undefined);
      setShowRequiredMessageBack(selectedDocBack === undefined);
      return;
    }
    setSubmitState('waiting');

    if (selectedDocFront && selectedDocBack) {
      const [documentFront, documentBack] = await Promise.all([
        convertFileToBase64(selectedDocFront),
        convertFileToBase64(selectedDocBack),
      ]);

      try {
        await uploadDocument({
          stripeEnv,
          gateway,
          documentFront: documentFront.base64String,
          type: 'id_document_or_passport',
          vaultId: kycId,
          documentBack: documentBack.base64String,
          stripeConnectAccountId,
          stripePersonId,
          fileType: documentFront.fileType,
        });
        setSubmitState('success');
        if (onSuccess !== undefined) {
          logger(`VerifyAccountForm PersonalIdentity success`);
          onSuccess();
        }
      } catch (e) {
        setSubmitState('error');
        const errors = e as string[];
        setSubmitErrors(
          errors.map((error) => ({
            message: error,
            details: JSON.stringify(e),
          })),
        );
      }
    }
  };

  return (
    <React.Fragment>
      {submitState ? (
        <ConfirmationPage currentState={submitState} errors={submitErrors} />
      ) : (
        <div>
          <div className='flex items-center my-2'>
            {selectedDocFront || selectedDocBack ? (
              <SnapIcon icon={'check-circle'} color={'#10B981'} size='md' />
            ) : (
              <SnapIcon icon={'exclamation'} color={'#F59E0B'} size='md' />
            )}
            <p className='mx-1 text-lg font-medium tracking-tight text-gray-900 text-center'>
              Personal Identity Documentation
            </p>
          </div>
          <div className='my-1'>
            <div className='text-sm font-normal leading-5'>
              Please choose to upload one of the following:
            </div>
            <div>
              {PERSONAL_IDENTITY_DOCS.map((doc, key) => (
                <Fragment key={key}>
                  <input
                    type='radio'
                    checked={selectedRadio === key}
                    onClick={(): void => setSelectedRadio(key)}
                    value={doc.value}
                    className='my-4'
                  />
                  <label className='text-sm font-medium'>{doc.label}</label>
                </Fragment>
              ))}
            </div>
          </div>
          <p className='page-text-secondary text-gray-700'>Front of ID</p>
          <FileUpload
            onFileChange={(f): void => {
              setSelectedDocFront(f);
              setShowRequiredMessageFront(false);
            }}
            onRemoveCurrentSelection={(): void =>
              setSelectedDocFront(undefined)
            }
            showRequiredMessage={showRequiredMessageFront}
          />
          <div className='my-8 border border-slate-200' />
          <p className='page-text-secondary text-gray-700'>Back of ID</p>
          <FileUpload
            onFileChange={(f): void => {
              setSelectedDocBack(f);
              setShowRequiredMessageBack(false);
            }}
            onRemoveCurrentSelection={(): void => setSelectedDocBack(undefined)}
            showRequiredMessage={showRequiredMessageBack}
          />
          <div className={!isMobile ? 'flex justify-end' : ''}>
            <button
              onClick={onSubmit}
              className='my-8 text-white text-lg lg rounded-full bg-blue-100 w-full'
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
